import { useState, type ReactNode } from "react";
import { useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { useNotify, useTranslate, type Identifier } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";

import { ListCardsSkeleton, ListErrorState } from "../design/ListStates";

/** Query key prefix of the screen; every write invalidates it. */
export const GUARDIAS_KEY = "guardias";

/** Shared input look: 44 px touch targets and a 16 px body on every width. */
export const FIELD_CLASS =
  "h-11 w-full rounded-md border bg-background px-3 text-base";

export const HELP_CLASS = "text-[15px] text-muted-foreground";

export interface GuardiasSectionProps {
  projectId: Identifier;
  /** Team Leader of this project, direccion or a bpo-scope lead_manager. */
  canEdit: boolean;
}

export interface SectionAdvisor {
  id: Identifier;
  name: string;
}

/**
 * Runs one write, refreshes the screen and reports the outcome in plain Spanish. Resolves to
 * null on success, or to the error message (the database validation text is already Spanish;
 * "" when the error carries none).
 */
export const useGuardiasSave = () => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);

  const run = async (
    write: () => Promise<unknown>,
    /** A form that shows the failure inline passes false, so it is not announced twice. */
    notifyError = true,
  ): Promise<string | null> => {
    setSaving(true);
    try {
      await write();
      await queryClient.invalidateQueries({ queryKey: [GUARDIAS_KEY] });
      notify("resources.guardias.saved", { type: "success" });
      return null;
    } catch (error) {
      const message = error instanceof Error ? error.message : "";
      if (notifyError) {
        notify(message || "resources.guardias.save_error", { type: "error" });
      }
      return message;
    } finally {
      setSaving(false);
    }
  };

  return { saving, run };
};

/** A titled card; the title is a sentence-case h2. */
export const SectionCard = ({
  title,
  help,
  children,
}: {
  title: string;
  help?: string;
  children: ReactNode;
}) => (
  <Card>
    <CardContent className="space-y-3 p-4">
      <h2 className="text-lg font-semibold">{title}</h2>
      {help ? <p className={HELP_CLASS}>{help}</p> : null}
      {children}
    </CardContent>
  </Card>
);

/** Skeleton while loading, an error state with retry, then the content. */
export const QueryBoundary = <T,>({
  query,
  children,
}: {
  query: UseQueryResult<T>;
  children: (data: T) => ReactNode;
}) => {
  const translate = useTranslate();
  if (query.error) {
    return (
      <ListErrorState
        message={translate("crm.common.list_error")}
        onRetry={() => query.refetch()}
      />
    );
  }
  if (query.data === undefined) return <ListCardsSkeleton rows={2} />;
  return <>{children(query.data)}</>;
};

/** "5 oct 2026, 09:30" in the Mexico City zone. */
export const formatInstant = (iso: string): string =>
  new Intl.DateTimeFormat("es-MX", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "America/Mexico_City",
  }).format(new Date(iso));
